import React, { useCallback, useContext } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled';
import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InformationIcon from '@atlaskit/icon/core/information-circle';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { useContentType, usePageSpaceKey } from '@confluence/page-context';
import { OperationTypes, useCanPerformContentOperation } from '@confluence/entity-operations';
import {
	SIDEBAR_DETAILS_PANEL_LOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	useShowDetailsPanel,
	preloadRedactionsQueries,
	useCanEditLabels,
} from '@confluence/details-panel';
import { useObjectSidebarActions, usePanel } from '@confluence/object-sidebar-api';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { EditLabelDialogLoader } from '@confluence/labels';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import {
	ShortcutVisualizer,
	GeneralShortcutListener,
	UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT,
	LABELS_SHORTCUT,
	UNIVERSAL_LABELS_SHORTCUT,
	VIEW_ATTACHMENTS_SHORTCUT,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN,
	isMac,
} from '@confluence/shortcuts';
import { ContentType } from '@confluence/content-types-utils';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { expVal } from '@confluence/feature-experiments';

import { OBJECT_SIDEBAR_TOUR_TARGETS } from '../objectSidebarTourTargets';

const i18n = defineMessages({
	openDetailsPanelLabel: {
		id: 'object-sidebar-components.open.details.panel.label',
		defaultMessage: 'Open Details Panel',
		description: 'Label for button to open the details panel',
	},
	showDetailsLabel: {
		id: 'object-sidebar-components.show.details.label',
		description:
			"The text is shown in a tooltip when the Object Sidebar's Details button is hovered.",
		defaultMessage: 'Show details',
	},
});

export const DetailsPanelButton = ({
	contentId,
	collapsed,
	isDisabled = false,
}: {
	contentId: string;
	collapsed?: boolean;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { showDetailsPanel } = useShowDetailsPanel();
	const [contentTypeFromState] = useContentType();
	// keyboard events do not bubble up to confluence when focus is in the iframe.
	// Since the keyboard shortcut does not work, we must hide the shortcut text.
	const isShortcutTextShown = contentTypeFromState !== ContentType.DATABASE;
	const panel = usePanel();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const [spaceKey = ''] = usePageSpaceKey();
	const { showModal } = useDialogs();
	const canEditLabels = useCanEditLabels();
	const { push } = useRouteActions();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const [canUpdateContent] = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType: contentTypeFromState,
		operation: OperationTypes.UPDATE,
	});

	const handleClickDetailsButton = useCallback(() => {
		experienceTracker.start({
			name: SIDEBAR_DETAILS_PANEL_LOAD_EXPERIENCE,
		});
		showDetailsPanel();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'detailsPanel',
				source: 'contentWrapper',
				attributes: {
					contentType: contentTypeFromState,
				},
			},
		}).fire();
	}, [showDetailsPanel, createAnalyticsEvent, experienceTracker, contentTypeFromState]);

	// Avoids CLS from redactions feature entitlement query in DetailsPanel
	const onHoverDetailsButton = useCallback(() => {
		if (fg('dlp_cc-redactions-metadata-modal')) {
			void preloadRedactionsQueries({
				contentId,
				canUpdateContent: !!canUpdateContent,
			});
		}
	}, [contentId, canUpdateContent]);

	const toggleDetailsPanel = useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault();
			if (panel?.id === 'detailsPanel') {
				void hideObjectSidebar();
			} else {
				handleClickDetailsButton();
			}
		},
		[handleClickDetailsButton, hideObjectSidebar, panel?.id],
	);

	const openLabelsDialog = useCallback(() => {
		showModal(EditLabelDialogLoader, {
			contentId,
			spaceKey,
			source: 'detailsPanelKeyboardShortcut',
		});
	}, [showModal, contentId, spaceKey]);

	const shortcutListeners = (
		<>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT}
				listener={toggleDetailsPanel}
			/>
			{canEditLabels && (
				<GeneralShortcutListener
					deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? LABELS_SHORTCUT : undefined}
					accelerator={UNIVERSAL_LABELS_SHORTCUT}
					listener={openLabelsDialog}
				/>
			)}
			<GeneralShortcutListener
				deprecatedAccelerator={
					!(isLiveEditMode || isOnEditRoute) ? VIEW_ATTACHMENTS_SHORTCUT : undefined
				}
				accelerator={
					isMac()
						? UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC
						: UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN
				}
				listener={() =>
					push(`${CONTEXT_PATH}/pages/viewpageattachments.action?pageId=${contentId}`)
				}
			/>
		</>
	);

	if (collapsed) {
		return shortcutListeners;
	}

	return (
		<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON}>
			<Box
				onMouseEnter={onHoverDetailsButton}
				data-vc="object-sidebar-details-button"
				testId="object-sidebar-details-button-id"
			>
				<Tooltip
					position="left"
					content={
						isShortcutTextShown ? (
							<ShortcutVisualizer
								shortcut={UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT}
								contentBefore={formatMessage(i18n.showDetailsLabel)}
								isTooltip
							/>
						) : (
							<FormattedMessage {...i18n.showDetailsLabel} />
						)
					}
					delay={0}
				>
					<IconButton
						onClick={handleClickDetailsButton}
						appearance="subtle"
						shape={isRovoButtonEnabled ? 'default' : 'circle'}
						icon={InformationIcon}
						label={formatMessage(i18n.openDetailsPanelLabel)}
						testId="sidebar-details-button"
						isDisabled={
							isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
						}
					/>
					{shortcutListeners}
				</Tooltip>
			</Box>
		</SpotlightTarget>
	);
};
